import { useMemo, type FC } from 'react'
import styled from 'styled-components'
import { CLIENT_NAME, COUNTRY_CODE, getDynamicText, IS_HM_STORE, IS_MMF_STORE, LANG, LANG_REGION, STORE_ABBR } from '@/utils'
import { createUnderline, designGrid, mq, presets, vw } from '@/styles'
import { Img, Link } from '../../base'
import { SocialMedia } from './SocialMedia'
import { ContactUs } from './footer/ContactUs'
import { useLocation, useModal, useSession, useTranslator } from '@/hooks'
import { MODALS } from '@/context'
import { Button } from '../../base/Button'
import { CallUs } from './footer/CallUs'
// import dynamic from 'next/dynamic'
// import { PreFooter } from './PreFooter'
import { TypeCmsLayout } from '@/schema'

// const DynamicFormNewsletter = dynamic(
//   () => import('../forms/FormNewsletter').then((mod) => mod.FormNewsletter),
//   { ssr: false }
// )

type Props = {
	firstCategory?: string
  pages: TypeCmsLayout['footers']
}

const getPages = (data) =>
  data.map((page, idx) => {
    if (!page) return
    if (!page?.url?.slug?.[LANG]) return
    const { title, url } = page

    return (
      <li key={idx}>
        <Link href={`/page/${url.slug[LANG]}`}>{title}</Link>
      </li>
    )
  })

const LOGOS_MMF = [
  {
    src: '/images/svg/logo-50-top.svg',
    alt: 'Logo 50 Top',
    href: '/page/50-top-pizza ',
  },
  {
    src: '/images/svg/logo-ilif.svg',
    alt: 'Logo I Love Italian Food',
  },
  {
    src: '/images/svg/logo-ffm.svg',
    alt: 'Logo 50 Top',
  },
]

const LOGOS_HM = {
  en: [
    {
      src: '/images/svg/logo-conde.svg',
      alt: 'Logo Condé Nast Traveler',
    },
    {
      src: '/images/svg/logo-ifn.svg',
      alt: 'Logo Italian Food Net',
    },
    {
      src: '/images/svg/logo-mail.svg',
      alt: 'Logo Mail Online',
    },
  ],
  es: [
    {
      src: '/images/svg/logo-conde.svg',
      alt: 'Logo Condé Nast Traveler',
    },
    {
      src: '/images/svg/logo-ifn.svg',
      alt: 'Logo Italian Food Net',
    },
    {
      src: '/images/svg/logo-mail.svg',
      alt: 'Logo Mail Online',
    },
    {
      src: '/images/svg/logo-el-espanol.svg',
      alt: 'Logo El Español',
    },
  ],
  fr: [],
}

const LEGAL_PAGE_HM = {
  en: null,
  es: {
    href: '/page/aviso-legal',
    name: 'Aviso legal'
  },
  fr: {
    href: '/page/mentions-legales',
    name: 'Avis juridique'
  },
}

const LEGAL_PAGE_MMF = {
  en: {
    href: '/page/legal-notice',
    name: 'Legal warning'
  },
  'es-ES': {
    href: '/page/aviso-legal',
    name: 'Aviso legal'
  },
  'es-CT': {
    href: '/page/aviso-legal',
    name: 'Aviso legal'
  },
  'es-MD': {
    href: '/page/aviso-legal',
    name: 'Aviso legal'
  },
  'es-CN': null,
  'fr-FR': {
    href: '/page/mentions-legales',
    name: 'Mentions légales'
  },
  'pt-PT': {
    href: '/page/aviso-legal',
    name: 'Aviso legal'
  }
}

const getFeaturedLogos = (lang) => {
  const data = IS_MMF_STORE ? LOGOS_MMF : LOGOS_HM[lang]

  return data.map((logo, idx) => {
    return (
      <li key={idx}>
        {logo?.href ? (
          <Link href={logo?.href}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={logo?.src} alt={logo?.alt} />
          </Link>
        ) : (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={logo?.src} alt={logo?.alt} />
        )}
      </li>
    )
  })
}

const FeaturedLogos = getFeaturedLogos(LANG)
const ariaControlsRegion = IS_MMF_STORE ? MODALS.REGION_STORE : MODALS.REGION

export const Footer: FC<Props> = ({ pages = [], firstCategory = '' }) => {
  const { toggle } = useModal(ariaControlsRegion)
  const { t } = useTranslator()
  const { user } = useSession()
  const Pages = useMemo(() => getPages(pages), [pages])
  const { location } = useLocation()

  return (
    <>
      {/* {!IS_MMF_STORE && <PreFooter />} */}
      <FooterStyled className={`${STORE_ABBR}`}>
        {IS_MMF_STORE ? <ContactUs /> : <CallUs />}
        {IS_MMF_STORE && (
          <>
            {/* <DynamicFormNewsletter /> */}
            <nav aria-labelledby='footer-main-nav'>
              <h2 id='footer-main-nav' className='srt-only'>
                Main Navigation
              </h2>
              <ul>
                <li>
                  <Link href={t('project.nav_menu.home.pathname') as string}>{t('project.nav_menu.home.name') as string}</Link>
                </li>
                <li>
                  <Link href={firstCategory ? `/${user ? 'shop' : 'catalog'}/${firstCategory}` : t('project.nav_menu.products.pathname') as string}>{t('project.nav_menu.products.name') as string}</Link>
                </li>
                {!!user && <li>
                  <Link href={t('project.nav_menu.my_usuals.pathname') as string}>{t('project.nav_menu.my_usuals.name') as string}</Link>
                </li>}
                <li>
                  <Link href={t('project.nav_menu.catalogue.pathname') as string}>{t('project.nav_menu.catalogue.name') as string}</Link>
                </li>
                <li>
                  <Link href={t('project.nav_menu.contact_us.pathname') as string}>{t('project.nav_menu.contact_us.name') as string}</Link>
                </li>
              </ul>
            </nav>
          </>
        )}
        <nav aria-labelledby='footer-other-links'>
          <h2 id='footer-other-links' className='srt-only'>Useful Links</h2>
          <ul>
						{COUNTRY_CODE === 'FR' && <li key='00-fr'>
							<a href='https://mammafiore.flatchr.io/fr/company/mammafiore/' target='_blank' rel='noopener noreferrer'>Rejoignez-nous</a>
						</li>}
            {Pages}
            {COUNTRY_CODE === 'ES' && <li key='canal-denuncias'>
              <a href='https://peoplemammafiore.factorial.es/complaints' target='_blank' rel='noopener noreferrer'>Canal de denuncias</a>
            </li>}
          </ul>
        </nav>
        <div className='stamp'>
          <Img media={mediaStamp} />
          {IS_MMF_STORE && (
            <>
              <Img media={ecoStamp} />
              <Img media={mozzarellaStamp} />
            </>
          )}
        </div>
        {FeaturedLogos.length > 0 && (
          <div className={`row-partners ${STORE_ABBR}`}>
            <span>
              {' '}
              {IS_MMF_STORE ? t('project.partners') : t('project.featured')}
            </span>
            <ul>{FeaturedLogos}</ul>
          </div>
        )}
        <p className='copyright'>
          <span>
            © {CLIENT_NAME} {new Date().getFullYear()}
          </span>
          <br />
          {false && LEGAL_PAGE_HM[LANG] && <Link href={LEGAL_PAGE_HM[LANG].href}>{LEGAL_PAGE_HM[LANG].name}</Link>}
          {IS_MMF_STORE && LEGAL_PAGE_MMF[LANG_REGION] && <Link href={LEGAL_PAGE_MMF[LANG_REGION].href}>{LEGAL_PAGE_MMF[LANG_REGION].name}</Link>}
        </p>
        <div>
          <SocialMedia />
          {!IS_HM_STORE &&
            <DivChangeRegion>
              {location && (
                <p>
                  {getDynamicText(t('project.you_are_in') as string, {
                    zone: location.name,
                  })}
                </p>
              )}
              <Button
                type='button'
                aria-controls={ariaControlsRegion}
                onClick={toggle}
                bgColor={null}
                color={'--color-white'}
                outline={true}
              >
                {t('project.change_zone')}
              </Button>
            </DivChangeRegion>
          }
        </div>
      </FooterStyled>
    </>
  )
}

const FooterStyled = styled.footer`
  ${designGrid({})}
  background-color: var(--color-text);
  color: var(--color-background);
  content-visibility: auto;
  padding: ${vw(70, 'mobile')} ${vw(20, 'mobile')} ${vw(50, 'mobile')};
  position: relative;
  z-index: 2;

  ${mq.greaterThan('nexus7')} {
    padding: ${vw(70, 'tablet')} ${vw(20, 'tablet')} ${vw(50, 'tablet')};
  }

  ${mq.greaterThan('tablet')} {
		grid-template-rows: auto 1fr auto auto;
    padding: ${vw(100, 'desktop')} 0 ${vw(60, 'desktop')};
  }

  ${mq.greaterThan('desktop')} {
    padding: 100px 0 60px;
  }

  nav {
    &[aria-labelledby="footer-main-nav"],
    &[aria-labelledby="footer-other-links"] {
      grid-column: 1 / span 3;
      margin: 60px 0 30px;

      ${mq.greaterThan('tablet')} {
        grid-row: 1 / span 2 !important;
        margin: 0 0 60px;
      }

      ul {
        align-items: flex-start;
        display: flex;
        flex-direction: column;
        gap: 16px;
        justify-content: flex-start;
      }

      ${mq.greaterThan('tablet')} {
        order: 1;
      }
    }

    &[aria-labelledby="footer-other-links"] {
      grid-column: 4 / span 3;
      grid-row: 3;

      ${mq.greaterThan('tablet')} {
        grid-column: auto / span 2;
      }

      a {
        ${presets.font15_1}
        font-family: var(--font-graphik);
        opacity: 1;
        text-decoration: none;
        transition: 300ms opacity ease-out;

        &:hover {
          opacity: 0.4;
        }
      }
    }

    &[aria-labelledby="footer-main-nav"] {
      grid-row: 3;

      ${mq.greaterThan('tablet')} {
        grid-column: 2 / span 4;
      }

      a {
        ${presets.font27_P18}
        color: var(--color-text--yellow);

        ${mq.greaterThan('tablet')} {
          ${presets.font53_2}
        }

        &.ac {
          color: var(--color-text);
        }
      }
    }
  }

  .copyright {
    ${presets.font15_2B}
    font-family: var(--font-graphik);
    grid-column: 1 / span 3;
    grid-row: 8;
    margin-top: 15px;

    ${mq.greaterThan('tablet')} {
      align-content: flex-end;
      align-items: flex-end;
      display: flex;
      flex-direction: row;
      gap: 20px;
      grid-column: 2 / span 4;
      grid-row: 6;
      margin-top: 45px;

      &,
      + div {
        order: 5;
      }
    }

    span {
      display: block;
      margin-bottom: 10px;

      ${mq.greaterThan('tablet')} {
        margin-bottom: 0;
      }
    }

    a {
      ${createUnderline({ color: 'var(--color-background)', reverse: true })};
    }

    + div {
      align-items: flex-start;
      display: flex;
      flex-direction: row;
      font-family: var(--font-graphik);
      gap: 20px;
      grid-column: 1 / span 6;
      grid-row: 6 / span 2;
      margin: 30px 0;
      justify-content: space-between;

      nav {
        margin-top: 15px;

        ${mq.greaterThan('tablet')} {
          margin-top: 0;
        }
      }

      ${mq.greaterThan('tablet')} {
        align-items: flex-end;
        grid-column: 6 / span 6;
        grid-row: 5 / span 2;
        justify-content: flex-start;
        margin: 50px 0 0;
      }

      button {
        height: 40px;
        padding: 0 20px;

        span {
          ${presets.font15}
          white-space: nowrap;
        }
      }
    }
  }

  .stamp {
    display: flex;
    flex-direction: row;
    gap: 15px;
    grid-column: 1 / span 3;
    grid-row: 5;
    justify-content: space-around;
    margin-right: auto;

    ${mq.greaterThan('tablet')} {
      align-items: flex-end;
      grid-area: unset;
      grid-column: 2 / span 3;
      grid-row: 3;
    }

    img {
      height: 60px;
      width: auto;

      ${mq.greaterThan('desktop')} {
        height: 85px;
      }
    }

    img {
      color: var(--color-text--yellow);
      mix-blend-mode: multiply;
      object-fit: contain;
			opacity: 0.5;
    }
  }

	&.hm {
		.row-partners {
			${mq.greaterThan('tablet')} {
				grid-column: 6 / span 7;
			}	
		}
	}

  .row-partners {
    align-items: flex-end;
    grid-column: 1 / span 6;
    grid-row: 4;
    margin-bottom: ${vw(25, 'mobile')};

    ${mq.greaterThan('tablet')} {
      grid-column: 6 / span 5;
			grid-row: 3;
      margin-bottom: 0;
    }

    > ul {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
    }

    span {
      ${presets.font15_1}
      color: var(--color-text--darker);
      display: block;
      mix-blend-mode: multiply;
      margin: ${vw(30, 'mobile')} 0 ${vw(10, 'mobile')};
      opacity: 0.5;

      ${mq.greaterThan('tablet')} {
        margin: 0 0 ${vw(25, 'desktop')};
      }

			${mq.greaterThan('desktop')} {
				margin: 0 0 25px;
			}
    }

    img {
      height: ${vw(15, 'mobile')};
      color: var(--color-text--yellow);
      margin-right: ${vw(20, 'mobile')};
      mix-blend-mode: multiply;
      object-fit: contain;
			opacity: 0.5;
      width: ${vw(77, 'mobile')};

      ${mq.greaterThan('tablet')} {
        height: ${vw(25, 'desktop')};
        margin-right: ${vw(40, 'desktop')};
        width: ${vw(150, 'desktop')};
      }
    }

    &.mmf,
    &.hm {
      img {
        height: ${vw(50, 'mobile')};
        width: auto;

        ${mq.greaterThan('tablet')} {
          height: ${vw(50, 'desktop')};
        }
      }
    }
  }
`

const DivChangeRegion = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  grid-column: 4 / span 3;
  margin-top: 15px;

  ${mq.greaterThan('tablet')} {
    align-items: center;
    flex-direction: row;
    grid-column: 9 / span 3;
    grid-row: 2 / span 1;
    margin-top: 0;
  }

  p {
    ${presets.font15_1}
    color: var(--color-white);
    margin-bottom: 8px;

    ${mq.greaterThan('tablet')} {
      margin-bottom: 0;
      margin-right: 10px;
    }

    ${mq.greaterThan('desktop-xs')} {
      margin-right: 16px;
    }
  }
`

const mediaStamp = {
  alt: 'Stamp denominazione origine logo',
  type: 'svg' as const,
  src: '/images/svg/i--stamp--denominazione-origine-protetta.svg',
  size: { width: 6, height: 8 },
  content_type: 'image/svg+xml'
} as const

const ecoStamp = {
  type: 'svg' as const,
  alt: 'Stamp evo certification',
  src: '/images/svg/i--eco.svg',
  size: { width: 275, height: 183 },
  content_type: 'image/svg+xml',
} as const

const mozzarellaStamp = {
  type: 'svg' as const,
  alt: 'Stamp Mozzarella Bufala',
  src: '/images/svg/i--mozzarella.svg',
  size: { width: 275, height: 183 },
  content_type: 'image/svg+xml',
} as const
